"use client";
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import dynamic from 'next/dynamic';
import DtvText, { IDtvTextProps } from "src/components/video-lab/dtv-text";
import ZipcodeModule, { ZipcodeModuleProps } from "src/components/video-lab/zipcode-module";
import { LocalRsnApiResponse } from "@directv-digital/dds.apis.local-rsn-chnls";
import PackageCmpGrid from './components/PackageComparisonGrid';
import { getChannelsData } from './utils/getChannels';
import { getSvodData } from './utils/getSVODS';
import { County } from "@directv-digital/dds.apis.county-lookup";

const ScrollToTop = dynamic(() => import('@directv-digital/dds.ui.scroll-to-top'), { ssr: false });
import { defaultSpacing, ssrStyle } from './customer-acq-compare-tool.styles';
import { getPriceOffers } from './utils/getPriceOffers';
import DynamicText, { DynamiceContentProps } from "@directv-digital/dds.ui.dynamic-text";
import { fireGa4ItemList } from "@directv-digital/dds.services.analytics-service";
import { setCookie } from "@directv-digital/dds.services.cookie-service";
import {
  COMPARED_PACKAGES_COOKIE,
  COMPARED_PACKAGES_LIFESPAN_DAYS,
  serializeComparedPackages
} from "lib/personalization/compared-packages";

export interface ChangeColumnTrayItem {
    title?: IDtvTextProps;
    selectionHint?: string;
    description?: string;
    tooltip?: string;
    multiSelect?: boolean;
    order?: number;
    type?: string;
    ribbon?: { content?: string; variant?: string; background?: string; color?: string };
}

export interface CustomerAcqCompareToolProps {
    id?: string;
    title?: IDtvTextProps;
    channels?: any;
    accordionData?: Array<any>;
    genreLocalChannelMapping?: Record<string, any>;
    signatureExcludedLocalChannels?: any[];
    accordionGenrePackBackgroundColor?: string;
    accordionGenrePackColor?: string;
    accordions?: any;
    rsnTierLegalText?: Record<string, any>;
    myfreeDtvDescription?: IDtvTextProps;
    embeddedSvods?: any;
    embeddedIncludedSvods?: any;
    getOffersGenreData?: any;
    alertText?: IDtvTextProps,
    offerPrices?: any;
    rsnFee?: string;
    genreHasRsnFee?: boolean;
    zipcodeConfig?: ZipcodeModuleProps['ciConfig'];
    genreNationals?: any;
    packagesConfig?: any;
    queryParameters?: Record<string, any>;
    genrePackOfferIds?: any;
    creditOffers?: any;
    changeColumnTray?: ChangeColumnTrayItem[];
    maxColumns?: string | number;
    columnConfig?:{
        columnLabel?: IDtvTextProps;
        compareLabel?: IDtvTextProps;
        changeColumnLabel?: IDtvTextProps;
        addMorePackLabel?: IDtvTextProps;
        cta?: {
            change?: {label : string};
            signup?: {label : string, signature: any, genreTargetUrl: any};
            close?: {label : string};
        };
        snackbarDurationMs?: number;
        snackbarGapMs?: number;
    };
    snackBarMessage?: {
        message?: string;
    };
    defaultPackages?: string[];
    legalText?: DynamiceContentProps;
    type?: string;
}

/**
 * Normalise the `?cmpPkg=` deep-link prop to a list of lowercased package tokens.
 *
 * The prop arrives in two shapes — a flat array of ids (what the adapter always
 * emits, `customer-acq-compare-adapter.ts:48-58`) or an object carrying `cmpPkg`
 * as an array or a comma-joined string. The displayPackages initializer and the
 * persistence effect both have to agree on what the link says, so they read it
 * through here instead of each parsing it themselves — those two disagreeing is
 * exactly what made an unresolvable link look like a real comparison.
 */
export function parseCmpPkgTokens(queryParameters?: any, maxColumns?: number): string[] {
  const sanitize = (val: any) => String(val ?? "").split("?")[0]?.replace(/['"]/g, "")?.trim()?.toLowerCase();

  const raw = Array.isArray(queryParameters) ?
    queryParameters :
    (Array.isArray(queryParameters?.cmpPkg) ?
      queryParameters?.cmpPkg :
      (typeof queryParameters?.cmpPkg === "string" ? queryParameters?.cmpPkg?.split(",") : []));

  const tokens = (raw || []).map(p => sanitize(p)).filter(Boolean);

  // Tokens past the column count are never rendered, so every caller that reasons
  // about what the customer SEES passes its cap here. A caller reading a wider
  // window than the initializer is precisely how `?cmpPkg=bad,bad,bad,mysports`
  // showed the CMS defaults and still counted as the customer's own comparison.
  return Number.isFinite(maxColumns) && maxColumns > 0 ?
    tokens.slice(0, Math.floor(maxColumns)) :
    tokens;
}

/**
 * Does a deep link name at least one package this CMS config actually knows?
 *
 * The presence of a token is NOT the same as a resolvable comparison. A stale
 * marketing URL, a renamed pack or a crawler can put `?cmpPkg=bogus` on the page;
 * those tokens match nothing, the initializer falls through to the CMS
 * `defaultPackages` fill, and the columns end up authored rather than chosen.
 * Matching mirrors the initializer's `matchPack` rule (id OR queryparam), minus
 * the internet/genre precedence, which only picks BETWEEN hits.
 *
 * `maxColumns` must be the SAME cap the initializer renders with. A token beyond
 * it never reaches a column, so it cannot make the displayed view "chosen": with
 * a wider window here, `?cmpPkg=bad,bad,bad,mysports` rendered the CMS defaults
 * yet skipped the baseline guard, writing those defaults over a real earlier
 * comparison on a zero-interaction load.
 */
export function hasResolvableCmpPkg(
  queryParameters?: any,
  packagesConfig?: any[],
  maxColumns?: number
): boolean {
  const wanted = new Set(parseCmpPkgTokens(queryParameters, maxColumns));
  if (wanted?.size === 0) {
    return false;
  }
  return (packagesConfig || []).some(pkg => wanted.has(String(pkg?.queryparam ?? "").toLowerCase()) ||
    wanted.has(String(pkg?.id ?? "").toLowerCase()));
}

export function CustomerAcqCompareTool(props: CustomerAcqCompareToolProps) {
  // Generated by Copilot- Start - 2026-04-08 15:28:21
  const packagesConfig = props?.packagesConfig || [];
  const genrePackages = useMemo(() => (packagesConfig || []).filter(pkg => pkg?.type === 'genre' && pkg?.subType !== 'multi'), [packagesConfig]);
  const miniPackages = useMemo(() => (packagesConfig || []).filter(pkg => pkg?.type === 'genre-mini'), [packagesConfig]);
  // Multi packs: type 'genre' WITH subType 'multi' — shown under Multi Pack section only.
  const genreMultiPackages = useMemo(() => (packagesConfig || []).filter(pkg => pkg?.type === 'genre' && pkg?.subType === 'multi'), [packagesConfig]);
  // Generated by Copilot- End - 2026-04-08 15:28:21
  // Merge signature and genre offerPrices into a single priceMap for fast lookup
  const normalizeKey = (key: string) => key.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

  const priceMap = useMemo(() => {
    const map: Record<string, number> = {};
    (props.offerPrices?.offerPrices || []).forEach(obj => {
      Object.entries(obj).forEach(([key, priceVal]) => {
        const normalizedKey = normalizeKey(`ATT_TV_${key}`);
        if (priceVal !== undefined && !isNaN(Number(priceVal))) {
          map[normalizedKey] = Number(priceVal);
        }
      });
    });
    (props.getOffersGenreData?.offerPrices || []).forEach(obj => {
      Object.entries(obj).forEach(([key, val]: [string, any]) => {
        if (val?.price !== undefined && !isNaN(Number(val.price))) {
          const normalizedKey = normalizeKey(key);
          map[normalizedKey] = Number(val.price);
        }
      });
    });
    return map;
  }, [props.offerPrices, props.getOffersGenreData]);

  try {
    const {
      id = props?.id || "customer-acq-compare-tool",
      title,
      channels,
      accordionData: _accordionDataProp,
      genreLocalChannelMapping,
      signatureExcludedLocalChannels,
      accordionGenrePackBackgroundColor,
      accordionGenrePackColor,
      accordions,
      rsnTierLegalText,
      myfreeDtvDescription,
      embeddedSvods,
      embeddedIncludedSvods,
      getOffersGenreData,
      alertText: _alertText,
      offerPrices,
      rsnFee: _rsnFee,
      zipcodeConfig,
      genreNationals,
      queryParameters,
      maxColumns = 3,
      legalText,
      defaultPackages = [] as string[]
    } = props;
    const [accordionData, setAccordionData] = useState<any[]>([]);
    const [_agentRsnFee, setAgentRsnFee] = useState<string>(props.rsnFee || "0");
    const [localRsnChannels, setLocalRsnChannels] = useState<LocalRsnApiResponse | null>(null);
    const [zipcodeDma, setZipcodeDma] = useState<string>("");
    const [_isLoading, setIsLoading] = useState<boolean>(false);
    const [_rsnError, setRsnError] = useState<boolean>(false);
    const [_loading, setLoading] = useState(true);
    const [_fetchedZipcodeModuleData, setFetchedZipcodeModuleData] = useState(null);

    // Generated by Copilot- Start - 2026-02-24
    const [displayPackages, setDisplayPackages] = useState<any[]>(() => {
      // eslint-disable-next-line no-underscore-dangle
      const _parsed = typeof maxColumns === 'string' ? parseInt(maxColumns, 10) : (maxColumns as number);
      const maxCols = Number.isFinite(_parsed) && _parsed > 0 ? _parsed : 3;
      const emptyPlaceholders = Array.from({ length: maxCols }, () => ({ isCompare: true }));

      if (!Array.isArray(packagesConfig) || packagesConfig.length === 0) {
        return emptyPlaceholders;
      }

      const qpPackages: string[] = parseCmpPkgTokens(queryParameters, maxCols);
      const matchPack = (packs: any[], id: string) => {
        if (!Array.isArray(packs) || !id) {
          return undefined;
        }
        const target = id.toLowerCase();
        const matches = packs.filter(pkg => {
          const queryparam = String(pkg?.queryparam || '').toLowerCase();
          const pkgId = String(pkg?.id || '').toLowerCase();
          return target === queryparam || target === pkgId;
        });
        return (
          matches.find(pkg => pkg.type === 'internet') ||
                    matches.find(pkg => pkg.type === 'genre') ||
                    matches[0]
        );
      };

      const getKey = (pkg: any) => (pkg?.id || pkg?.queryparam || '').toLowerCase();
      const result: any[] = [];
      const seenKeys = new Set<string>();
      qpPackages.forEach(id => {
        const found = matchPack(packagesConfig, id);
        if (!found) {
          return;
        }
        const key = getKey(found);
        if (found.type === 'internet' && seenKeys.has(key)) {
          return;
        }
        seenKeys.add(key);
        result.push(found);
      });
      if (result.length < 2) {
        for (const defId of defaultPackages) {
          if (result.length >= 2) {
            break;
          }
          const defPkg = matchPack(packagesConfig, defId?.toLowerCase());
          if (!defPkg) {
            continue;
          }
          const key = getKey(defPkg);
          if (seenKeys.has(key)) {
            continue;
          }
          seenKeys.add(key);
          result.push(defPkg);
        }
      }

      const matched = result.slice(0, maxCols);
      return matched.length > 0 ? matched : emptyPlaceholders;
    });
    // Generated by Copilot- End - 2026-02-24

    const hasDisplayPackages = useMemo(() => (displayPackages || [])?.length > 0, [displayPackages]);

    // State for columnSelections (chip tray selections)
    const [columnSelections, setColumnSelections] = useState<Record<number, { genre: string[], signature: string[] }>>({});
    // State for miniSelectionsByColumn (mini pack selections)
    const [miniSelectionsByColumn, setMiniSelectionsByColumn] = useState<Record<number, string[]>>({});

    // Build the data for the grid, including price accordion
    const buildGridData = useCallback(async localRsnChannels => {
      try {
        setIsLoading(true);
        const currentPackagesByColumn = getActivePackagesByColumn();
        const activePackagesByColumn =
                    currentPackagesByColumn && currentPackagesByColumn.length > 0 && currentPackagesByColumn.some(col => col.length > 0) ?
                      currentPackagesByColumn :
                      [displayPackages.filter(pkg => !pkg?.isCompare && !pkg?.isEmpty)];
        const allSelectedPackages = activePackagesByColumn.flat();
        const allSelectedSignaturePackageIds = allSelectedPackages
          .filter(pkg => pkg.type === 'signature' || pkg.type === 'internet' || pkg.type === 'satellite')
          .map(pkg => pkg.id);
        const priceRows = priceMap && accordions ?
          getPriceOffers(accordions, priceMap, (displayPackages || []).filter(pkg => !pkg?.isCompare && !pkg?.isEmpty), columnSelections,
            miniSelectionsByColumn, maxColumns, getOffersGenreData?.offerPrices, offerPrices?.offerPrices || [],
            props.creditOffers, props.genrePackOfferIds, packagesConfig) :
          [];
        const channelsNestedSections = accordions?.channels?.nestedSections || [];
        const channelsSection = channelsNestedSections.find(sec => sec.type === "channels");
        const channelCountsLabel = channelsSection && channelsSection.rows ?
          ((Object.values(channelsSection.rows).find(row => (row as { type?: string; label?: string }).type === "channelCount") as { label?: string })?.label || "Channel counts") :
          "Channel counts";
        const channelPayload = await getChannelsData(
          {
            channels: channels || [],
            localRsnChannels
          },
          allSelectedPackages,
          packagesConfig,
          {
            genreLocalChannelMapping: genreLocalChannelMapping || {},
            signatureExcludedLocalChannels,
            accordionGenrePackBackgroundColor,
            accordionGenrePackColor,
            channelsAccordionName: accordions?.channels?.accordionLabel,
            channelsAccordionTextColor: accordions?.channels?.textColor,
            channelsAccordionBackgroundColor: accordions?.channels?.backgroundColor,
            expand: accordions?.channels?.expand,
            zipcodeDma,
            rsnTierLegalText: rsnTierLegalText || {},
            embeddedSvods,
            embeddedIncludedSvods,
            getOffersGenreData,
            genreNationals,
            maxColumns,
            channelCountsLabel,
            columnSelections,
            miniSelectionsByColumn
          }
        );

        const svodData = await getSvodData(
          allSelectedPackages,
          packagesConfig,
          {
            svodAccordionName: accordions?.channels?.nestedSections[0]?.label,
            accordionHeading: accordions?.channels?.accordionHeading,
            channelsAccordionBackgroundColor: accordions?.channels?.backgroundColor,
            rsnTierLegalText: rsnTierLegalText || {},
            embeddedSvods,
            embeddedIncludedSvods,
            getOffersGenreData,
            signaturePkgIds: allSelectedSignaturePackageIds,
            genreNationals,
            maxColumns,
            columnSelections,
            miniSelectionsByColumn
          }
        );
        // Always merge price, SVOD, channels, and myFreeDtvAccordion if present, regardless of channelPayload
        const myFreeDtvPayload = channelPayload && (channelPayload as any)?.myFreeDtvAccordion ? (channelPayload as any)?.myFreeDtvAccordion : null;
        const channelAccordion = channelPayload && (channelPayload as any)?.channels ? (channelPayload as any)?.channels : null;
        // Combine SVOD and channel sections into nestedSections for the channels accordion
        const channelsNestedSection = accordions?.channels?.nestedSections?.find(sec => sec.type === "channels");
        const nestedSections = [
          svodData,
          channelAccordion && channelAccordion.accordionDetails ?
            {
              label: channelsNestedSection?.label,
              type: "channels",
              rows: channelAccordion.accordionDetails[0]?.row || []
            } :
            null
        ].filter(Boolean);

        const includedChannelsAccordion = {
          accordionType: "channels",
          accordionHeading: channelAccordion?.accordionHeading,
          backgroundColor: channelAccordion?.accordionHeading?.backgroundColor,
          textColor: channelAccordion?.accordionHeading?.color,
          nestedSections,
          expand: channelAccordion?.expand
        };

        // Format MyFree DTV accordion as a flat Channels accordion
        const myFreeDtvAccordion = myFreeDtvPayload && myFreeDtvPayload.accordionDetails?.[0]?.row?.length > 0 ?
          {
            accordionType: "Channels",
            packageId: "MYFREE_DTV",
            accordionHeading: myFreeDtvPayload.accordionHeading,
            backgroundColor: myFreeDtvPayload.accordionHeading?.backgroundColor,
            textColor: myFreeDtvPayload.accordionHeading?.color,
            accordionDetails: myFreeDtvPayload.accordionDetails,
            expand: false
          } :
          null;

        const mergedAccordions = [
          ...priceRows,
          includedChannelsAccordion,
          myFreeDtvAccordion
        ].filter(Boolean);

        setAccordionData(mergedAccordions);
      } catch (error) {
        console.error("CustomerAcqCompareTool: failed to build grid", error);
        setAccordionData([]);
      } finally {
        setIsLoading(false);
      }
    }, [
      accordions,
      channels,
      embeddedIncludedSvods,
      embeddedSvods,
      genreLocalChannelMapping,
      signatureExcludedLocalChannels,
      genreNationals,
      getOffersGenreData,
      offerPrices,
      rsnTierLegalText,
      displayPackages,
      zipcodeDma,
      localRsnChannels,
      columnSelections,
      miniSelectionsByColumn
    ]);
    const getActivePackagesByColumn = useCallback(() => {
      const { packagesConfig = [] } = props;
      return Object.keys(columnSelections).map(colIdx => {
        const sel = columnSelections[colIdx] || {
          genre: [],
          signature: []
        };
        const pkgs: any[] = [];
        sel.signature?.forEach(sigId => {
          const found = packagesConfig.find(pkg => pkg.id === sigId);
          if (found) {
            pkgs.push(found);
          }
        });
        sel.genre?.forEach(genreId => {
          const found = packagesConfig.find(pkg => pkg.id === genreId);
          if (found) {
            pkgs.push(found);
          }
        });
        const miniIds = miniSelectionsByColumn[Number(colIdx)] || [];
        miniIds.forEach(miniId => {
          const found = packagesConfig.find(pkg => pkg.id === miniId);
          if (found) {
            pkgs.push(found);
          }
        });
        return pkgs;
      });
    }, [columnSelections, miniSelectionsByColumn, props.packagesConfig]);
    const onZipcodeUpdateClick = useCallback(
      (
        _zipcode: string,
        _counties: County[] | null,
        rsnFeeVal?: string,
        localRsnChnls?: LocalRsnApiResponse | null,
        _county?: County
      ) => {
        setAgentRsnFee(() => (rsnFeeVal && parseFloat(rsnFeeVal) > 0 ? rsnFeeVal : "0"));
        setLocalRsnChannels(() => localRsnChnls || null);
      },
      []
    );

    const addLocalRsnChnls = React.useCallback(
      (
        channels: LocalRsnApiResponse | null,
        dma?: string,
        _nielsenDma?: string
      ) => {
        setZipcodeDma(dma);
        const { localChannels = [], rsnChannels = [] } = channels || {};
        console.log(
          "Customer Acq Compare tool fetched LocalRsn Chnls >>",
          channels,
          localChannels,
          rsnChannels
        );
        setLocalRsnChannels(channels);
        setLoading(false);
      },
      []
    );
    const onCountySearchDone = useCallback(
      (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null, county?: County) => {
        setFetchedZipcodeModuleData(() => ({
          zipcode,
          counties,
          rsnFee,
          localRsnChnls,
          county
        }))
      }
      , []
    );
    const handleRsnFeeApiError = error => {
      setAccordionData([]);
      buildGridData(localRsnChannels);
      setRsnError(true);
      console.error("Customer Acq Compare tool >> rsn fee API Error..", error);
    }
    const updateRsnFee = (fee: string) => {
      if (fee && fee !== "" && parseFloat(fee) > 0) {
        setAgentRsnFee(fee);
      } else {
        console.error(`[CustomerCmptool] Zipcode Module returned a faulty rsn fee '${fee}' under valid condition. This is a defect.`);
        setAgentRsnFee("0");
      }
    };
    const handleCountyApiError = error => {
      setLoading(false);
      buildGridData(localRsnChannels)
      console.error("Customer Acq Compare tool >> county API Error..", error);
    }
    const handleLocalRsnApiError = error => {
      setLoading(false);
      buildGridData(localRsnChannels)
      console.error("Customer Acq Compare tool >> Local/Rsn API Error..", error);
    };
    // Generated by Copilot- Start - 2026-02-24
    // WAT-9622 AC 3a: Fire view_item_list exactly once when displayPackages first becomes available
    const hasImpressionFired = React.useRef(false);
    const ITEM_LIST_NAME = "package_comparison";
    useEffect(() => {
      if (!hasImpressionFired.current && displayPackages?.length > 0) {
        const impressions = displayPackages
          .filter(pkg => pkg && pkg.id)
          .map((pkg, index) => ({
            item_id: (pkg?.id || "").toLowerCase(),
            item_name: (pkg?.displayLabel || pkg?.name || "").toLowerCase(),
            item_category: (pkg?.type || "").toLowerCase(),
            price: pkg?.price ?? "",
            index: pkg?.order ?? index,
            item_list_name: ITEM_LIST_NAME

          }));
        if (impressions.length === 0) {
          return;
        }
        hasImpressionFired.current = true;
        window.dataLayer = (window as any).dataLayer || [];
        fireGa4ItemList("", null);
        fireGa4ItemList("view_item_list", impressions);
      }
    }, [displayPackages]);
    // Generated by Copilot- End - 2026-02-24
    useEffect(() => {
      if (hasDisplayPackages) {
        const timer = setTimeout(() => {
          buildGridData(localRsnChannels);
        }, 300);
        return () => clearTimeout(timer);
      }
      setAccordionData([]);
    }, [buildGridData, hasDisplayPackages, localRsnChannels, columnSelections, miniSelectionsByColumn]);

    // One scan shared by both consumers below. miniSelectionsByColumn is in
    // getActivePackagesByColumn's own dep list, so mini-pack changes flow through
    // here too - dropping it there would silently stop the cookie updating.
    const selectedPackagesByColumn = useMemo(
      () => getActivePackagesByColumn(),
      [getActivePackagesByColumn]
    );

    const activePackagesByColumn = useMemo(() => {
      const currentPackagesByColumn = selectedPackagesByColumn;
      return currentPackagesByColumn && currentPackagesByColumn.length > 0 && currentPackagesByColumn.some(col => col.length > 0) ?
        currentPackagesByColumn :
        [displayPackages.filter(pkg => !pkg?.isCompare && !pkg?.isEmpty)];
    }, [selectedPackagesByColumn, displayPackages]);
    const allSelectedPackages = useMemo(() => activePackagesByColumn.flat(), [activePackagesByColumn]);

    // WAT-11464 — the comparison column structure remembered for personalization.
    //
    // Deliberately NOT `activePackagesByColumn` above: that memo collapses the
    // default view into ONE column and every caller flattens it, so its column
    // shape is meaningless. Here column POSITION is the signal (AC3/AC12).
    //
    // 🔴 getActivePackagesByColumn() returns a DENSE array built from
    // `Object.keys(columnSelections)`, and PackageCmprGrid seeds a key only for
    // 'genre'/'internet' packages — a cleared column loses its key entirely, and
    // signature/satellite columns never get one. So its index is NOT the column
    // number. Re-key onto the real column index and size the result from
    // displayPackages (one entry per rendered column) so a cleared or
    // satellite-only column keeps its slot instead of shifting its neighbours left.
    const comparedColumnIds = useMemo<string[][]>(() => {
      // Mirror the grid's one layout special-case: a lone signature pack renders
      // CENTRED as [empty, pkg, empty] (PackageCmprGrid.tsx:239-243), so the customer
      // sees it in column 2 while displayPackages holds it at index 0. Recording
      // index 0 would store a position they never saw and mis-target the day
      // column-specific conditions ship - the wire format promises that structure.
      // isSignaturePack is `type === 'internet'` there; kept in step deliberately.
      if ((displayPackages || []).length === 1 && displayPackages?.[0]?.type === "internet") {
        const loneId = displayPackages?.[0]?.id;
        return loneId ? [[], [loneId]] : [];
      }

      // Reuses the same scan the sibling activePackagesByColumn memo performs under
      // identical deps, rather than re-running the nested packagesConfig.find() pass.
      const selectedByColumn = selectedPackagesByColumn;
      const selectionsByColumn = new Map<number, any[]>(
        Object.keys(columnSelections || {})?.map?.(
          (key, denseIndex) => [Number(key), selectedByColumn?.[denseIndex] ?? []]
        ) ?? []
      );

      // Trailing empty columns carry no signal, and they make the baseline
      // unstable: `displayPackages` only ever GROWS (every writer assigns in
      // place — there is no splice anywhere in the grid), and the default view
      // is 2 entries because the initializer stops at `result.length >= 2`
      // while the grid pads to 3 for rendering only. So adding a third-column
      // pick and removing it again leaves a 3-entry array whose serialization
      // ("A|B|") differs from the 2-entry baseline ("A|B") by string equality
      // alone — and the CMS defaults get written as if they were the customer's
      // comparison. The reader .flat()s that empty slot away anyway, so trimming
      // loses nothing: only leading/middle gaps are positionally meaningful.
      const columns = (displayPackages || []).map((displayed, columnIndex) => {
        // Key the fallback on PRESENCE, not emptiness. An empty array means the
        // customer emptied that column and it must serialize as an empty slot;
        // falling back on `length > 0` would re-record the package they just
        // removed. A genuinely unseeded column (signature/satellite, or before
        // the grid seeds state) has no key at all and does fall back.
        const column = selectionsByColumn.has(columnIndex) ?
          (selectionsByColumn.get(columnIndex) ?? []) :
          [displayed];
        return (column || [])
          .filter(pkg => !pkg?.isCompare && !pkg?.isEmpty)
          .map(pkg => pkg?.id)
          .filter(Boolean);
      });

      // findLastIndex is ES2023; this repo targets lower, so walk it back.
      let lastPopulated = columns.length - 1;
      while (lastPopulated >= 0 && !columns[lastPopulated]?.length) {
        lastPopulated -= 1;
      }
      return columns.slice(0, lastPopulated + 1);
    }, [selectedPackagesByColumn, columnSelections, displayPackages]);

    // AC1 — the comparison is stored as the customer builds it, NOT on exit.
    //
    // Persisting on exit reads as the more faithful "capture when they leave", but it
    // lands too late to be useful: `pagehide` fires only once the NEXT document
    // commits, and a Pages-Router client navigation fetches `/_next/data` before the
    // old tree unmounts. In both cases the request that `checkComparedPackages`
    // evaluates has already gone out without the cookie, so the very page the
    // customer navigates to is never personalized. Exit events also never fire at all
    // when a mobile OS discards a backgrounded tab, silently losing the comparison.
    //
    // Writing on change does NOT personalize the page they are on — that page was
    // already server-rendered — so AC1's "only after they leave" still holds. It just
    // makes the value available to the next server render, which is the entire point.
    // maxColumns is CMS-configurable, so the codec must not silently truncate at its
    // own fixed default: an author setting 4 would lose column 4 on write with no
    // guard and no error. Parsed the same way the displayPackages initializer does.
    const maxComparedColumns = useMemo(() => {
      const parsed = typeof maxColumns === 'string' ? parseInt(maxColumns, 10) : (maxColumns as number);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 3;
    }, [maxColumns]);

    // A deep link only counts as the customer's own comparison when at least one of
    // its tokens RESOLVED to a package. This used to ask only "was a token present",
    // which is a different question: an unresolvable link (stale marketing URL,
    // renamed pack, crawler) has tokens, matches nothing, and falls through to the
    // CMS defaultPackages fill in the initializer above — so treating it as chosen
    // wrote the CMS defaults to a 400-day cookie on a zero-interaction page load and
    // clobbered a genuine comparison from an earlier visit. Reads the link through
    // the same parser AND the same column window as the initializer, so the two
    // cannot drift apart again — a token past the cap is never displayed, so it
    // cannot make the displayed view "chosen".
    const hasDeepLinkedComparison = useMemo(
      () => hasResolvableCmpPkg(queryParameters, packagesConfig, maxComparedColumns),
      [queryParameters, packagesConfig, maxComparedColumns]
    );

    const baselineComparisonRef = useRef<string>(null);
    const persistedComparisonRef = useRef<string>("");
    useEffect(() => {
      const value = serializeComparedPackages(comparedColumnIds, maxComparedColumns);

      // First pass decides what counts as "the page handed me this" vs "I chose this".
      //
      // A ?cmpPkg= deep link IS the customer's own comparison: the compare tool is
      // launched from compare checkboxes on genre-pack, plan and flip cards, and those
      // build the URL (/compare-packages/?type=genre&cmpPkg=a,b). So there is no
      // baseline to protect — store it straight away, even if they never touch a chip.
      //
      // Without cmpPkg the columns come from the CMS defaultPackages list. That is
      // authored, not chosen, so it remains the baseline: storing it would tag every
      // visitor who merely loaded the page and would clobber a genuine comparison
      // from an earlier visit.
      if (baselineComparisonRef?.current === null) {
        baselineComparisonRef.current = hasDeepLinkedComparison ? "" : value;
        if (!hasDeepLinkedComparison) {
          return;
        }
      }

      // Undoing every change is "no comparison of their own", not a comparison of the
      // defaults — AC5 keeps only the final state, so that must clear, not persist.
      const effective = value === baselineComparisonRef?.current ? "" : value;
      if (effective === persistedComparisonRef?.current) {
        return;
      }
      persistedComparisonRef.current = effective;

      if (effective) {
        setCookie(COMPARED_PACKAGES_COOKIE, effective, "/", COMPARED_PACKAGES_LIFESPAN_DAYS, false);
        return;
      }
      // Nothing worth keeping — don't leave an undone comparison personalizing for
      // 400 days. Delete THROUGH setCookie rather than expireCookie: the DDS helper
      // resolves the domain from window.location.hostname on write, while
      // expireCookie defaults to ".directv.com", so on any host that resolves
      // differently the delete would miss the cookie it wrote and leave it live.
      // A truthy value plus a negative lifespan is the standard delete idiom and
      // inherits identical domain resolution.
      setCookie(COMPARED_PACKAGES_COOKIE, "0", "/", -1, false);
      // Deps are deliberately just the comparison. maxComparedColumns and
      // hasDeepLinkedComparison also derive from CMS props, but they are stable for
      // the life of the page, and hasDeepLinkedComparison is read only on the first
      // pass — re-running this effect when they change would re-evaluate the
      // baseline against a comparison the customer has since edited.
    }, [comparedColumnIds]);

    return (
            <>
                <DtvBox
                    id={`${id}-container`}
                    sx={{
                        ...ssrStyle?.root || {}
                    }}>
                    {title?.content &&
                        <DtvBox
                            id={`${id}-title`}
                            sx={{
                                ...ssrStyle?.titleContainer || {},
                                ...defaultSpacing || {}
                            }}
                        >
                            <DtvText
                                id={`${id}-title-text`}
                                variant="subhead1medium"
                                component="div"
                                content={title?.content}
                                role="heading"
                                aria-level={1}
                                sx={{
                                    color: title?.color || '#000000',
                                    ...ssrStyle?.titleContainer || {}
                                }}
                            />
                        </DtvBox>
                    }
                    <DtvBox sx={{
                        ...defaultSpacing || {},
                        ...ssrStyle?.zipCodeContainer || {}
                    }}>
                        <ZipcodeModule
                            id={`${id}-zipcode-module`}
                            mode={"light"}
                            size="medium"
                            position="left"
                            componentType="customer-acq-compare-tool"
                            ciConfig={zipcodeConfig}
                            onSearchButtonClick={onZipcodeUpdateClick}
                            onLocalRsnChnlsApiError={handleLocalRsnApiError}
                            onRsnFeeApiError={handleRsnFeeApiError}
                            onCountyApiError={handleCountyApiError}
                            onLocalRsnChnlsConfirmed={addLocalRsnChnls}
                            onRsnFeeConfirmed={updateRsnFee}
                            onCacheConfirmed={onCountySearchDone}
                            localRsnApiOptions={{
 satellite: false,
stream: true
}}
                        />
                    </DtvBox>
                    {hasDisplayPackages && (
                        <>
                        <PackageCmpGrid
                            id={id}
                            accordionsData={accordionData}
                            displayPackages={displayPackages}
                            setDisplayPackages={setDisplayPackages}
                            activePackagesByColumn={allSelectedPackages}
                            myfreeDtvDescription={myfreeDtvDescription}
                            packagesConfig={packagesConfig}
                            miniPackages={miniPackages}
                            priceMap={priceMap}
                            genrePackages={genrePackages}
                            multipackages={genreMultiPackages}
                            changeColumnTray={props?.changeColumnTray}
                            columnSelections={columnSelections}
                            setColumnSelections={setColumnSelections}
                            miniSelectionsByColumn={miniSelectionsByColumn}
                            setMiniSelectionsByColumn={setMiniSelectionsByColumn}
                            columnConfig={props?.columnConfig}
                            maxColumns={maxColumns}
                            type={(props?.type || '').toLowerCase()}
                            getOffersGenreData={getOffersGenreData}
                            snackBarMessage={props?.snackBarMessage}
                        />

                            {accordionData?.length > 0 && (
                            <DtvBox
                                sx={{
                                    borderRadius: '6px',
                                    padding: {
 xs: '15.5px 16px 20px 20px',
sm: '22px 24px 22px 46px',
md: "22px 100px 22px 144px"
},
                                    maxWidth: '100%'
                                }}
                            >
                                {legalText?.content && (
                                    <DynamicText
                                        id={`${props?.id}-legalText`}
                                        content={legalText?.content}
                                        component={legalText?.component || "div"}
                                        {...(legalText?.cta && {
                                            cta: {
                                                label: legalText.cta.label || "",
                                                legalUrl: legalText.cta.legalUrl || "",
                                                position: (legalText.cta.position === "inline" || legalText.cta.position === "block") ?
                                                    legalText.cta.position :
                                                    "inline"
                                            }
                                        })}
                                        sx={{
                                            color: legalText?.color || '#222',
                                            fontSize: legalText?.fontSize || '12px !important',
                                            // WAT-11565: this legal cta renders as <button> now, not <a>.
                                            '& a:focus, & button:focus': {
                                                outline: '2px solid #2E70BD',
                                                fontWeight: 'bold'
                                            }
                                        }}
                                    />
                                )}
                            </DtvBox>
                            )}
                        </>
                    )}

                    <ScrollToTop
                        showAlways={false}
                        backgroundColor="#FFF"
                        color="#102641"
                    />
                </DtvBox>
            </>
    );
  } catch (error) {
    console.error("Error in customer acq compare Tool >> ", error);
    return <ErrorDisplay error={error} component="customer acq compare Tool" />;
  }
}
